import {
  Box,
  Button,
  CircularProgress,
  Grid,
  LinearProgress,
  List,
  Typography,
} from '@mui/material';
import { forwardRef } from 'react';
import { ListProps, Virtuoso } from 'react-virtuoso';
import { makeStyles } from 'tss-react/mui';
import Theme from '../../../../Theme';
import PeopleGroupIcon from '../../../../icons/PeopleGroupIcon';
import LangEnum from '../../../../lang/LangEnum';
import { useApplicationContext } from '../../../context/ApplicationContext/ApplicationContext';
import { useTranslation } from '../../../translation/TranslationContext';
import { useChatContext } from '../../contexts/chat/chat.context';
import { ChatParticipant } from '../../types/chat.types';
import ChatParticipantListItem from './ChatParticipantListItem';

import ListItemButton from '@mui/material/ListItemButton';

const useStyles = makeStyles()((theme) => ({
  newGroupButton: {
    width: '100%',
    height: theme.spacing(7),
    padding: theme.spacing(2),
    borderRadius: '8px',
    marginBottom: '4px',
    display: 'flex',
    justifyContent: 'flex-start',
  },
  svgIcon: {
    marginBottom: theme.spacing(-1),
    fontSize: 32,
    marginRight: theme.spacing(1),
  },
}));

type ChatParticipantListProps = {
  loadMore: () => void;
  onClick: (participant: ChatParticipant) => void;
  participants: ChatParticipant[];
  isLoading: boolean;
  multiselect: boolean;
  selectedParticipantsLength: number;
};

const ChatParticipantList = ({
  loadMore,
  onClick,
  participants,
  isLoading,
  multiselect,
  selectedParticipantsLength,
}: ChatParticipantListProps) => {
  const { classes } = useStyles();

  const { translate } = useTranslation();

  const { isAdminBrand } = useApplicationContext();
  const { onClickCreateGroupChat } = useChatContext();

  const showCircularProgress = isLoading && participants.length === 0;
  const showLinearProgress = isLoading && participants.length > 0;

  return (
    <Box
      sx={{
        position: 'relative',
        height: '100%',
      }}
    >
      {showCircularProgress && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            marginLeft: -1 / 2,
          }}
        >
          <CircularProgress size={16} />
        </Box>
      )}
      <Virtuoso
        data={participants}
        endReached={loadMore}
        totalCount={participants.length}
        computeItemKey={(_, participant) => participant.id}
        style={{
          marginLeft: '16px',
          marginRight: '16px',
        }}
        components={{
          Header: () =>
            multiselect || !isAdminBrand ? null : (
              <Button className={classes.newGroupButton} onClick={onClickCreateGroupChat}>
                <Grid
                  container
                  sx={{
                    alignItems: 'center',
                  }}
                >
                  <Grid>
                    <PeopleGroupIcon className={classes.svgIcon} />
                  </Grid>
                  <Grid>
                    <Typography color="primary" style={{ fontWeight: 600 }}>
                      {translate(LangEnum.CHAT_ROOM_NEW_GROUP_BUTTON)}
                    </Typography>
                  </Grid>
                </Grid>
              </Button>
            ),
          List: forwardRef<HTMLDivElement, ListProps>(({ style, children }, listRef) => (
            <List style={{ ...style, margin: 0 }} component="div" ref={listRef}>
              {children}
            </List>
          )),

          Item: ({ children, ...props }) => (
            <>
              <ListItemButton
                component="div"
                {...props}
                onClick={() => onClick(participants[props['data-index']])}
                style={{
                  margin: 0,
                  marginBottom: '4px',
                  borderRadius: '8px',
                  backgroundColor:
                    props['data-index'] < selectedParticipantsLength
                      ? `${Theme.palette.primary.main}1A`
                      : 'none',
                }}
              >
                {children}
              </ListItemButton>
            </>
          ),
        }}
        itemContent={(index, participant) => {
          const isSelected = index < selectedParticipantsLength;
          return (
            <ChatParticipantListItem
              multiselect={multiselect}
              participant={participant}
              isSelected={isSelected}
            />
          );
        }}
      />
      {showLinearProgress && <LinearProgress />}
    </Box>
  );
};

export default ChatParticipantList;
